import {
  formatIsoDateToDisplay,
  getCurrentBusinessMonthRange,
  getTodayIso,
  parseDisplayDateToIso,
} from "./dateFilters";

const METRIC_FIELDS = [
  "producaoBrutaTotal",
  "producaoLiquidaTotal",
  "producaoComissionadaTotal",
  "contratosAprovados",
  "contratosNegados",
  "contratosCancelados",
  "contratosEmAndamento",
];

function parseIsoDate(value) {
  const iso = parseDisplayDateToIso(value);
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function getInclusiveMonthSpan(startDate, endDate) {
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) return 1;
  if (!(endDate instanceof Date) || Number.isNaN(endDate.getTime())) return 1;
  const diffMonths =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth()) +
    1;
  return Math.max(diffMonths, 1);
}

function toIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizeTipoProduto(value, fallback = "Sem tipo de produto") {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized || fallback;
}

export function sanitizeJoinPeriodo(period = {}, reference = new Date()) {
  const fallback = getCurrentBusinessMonthRange(reference);
  const todayIso = getTodayIso(reference);

  let dataInicio = parseDisplayDateToIso(period?.dataInicio) || fallback.from;
  let dataFim = parseDisplayDateToIso(period?.dataFim) || fallback.to;

  if (dataInicio > todayIso) dataInicio = todayIso;
  if (dataFim > todayIso) dataFim = todayIso;
  if (dataInicio > dataFim) dataInicio = dataFim;

  return { dataInicio, dataFim };
}

export function shiftIsoDateByMonths(value, months) {
  const source = parseIsoDate(value);
  if (!source) return "";

  const day = source.getDate();
  const firstDayTargetMonth = new Date(source.getFullYear(), source.getMonth() + months, 1);
  const lastDayTargetMonth = new Date(
    firstDayTargetMonth.getFullYear(),
    firstDayTargetMonth.getMonth() + 1,
    0
  ).getDate();

  return toIsoDate(
    new Date(
      firstDayTargetMonth.getFullYear(),
      firstDayTargetMonth.getMonth(),
      Math.min(day, lastDayTargetMonth)
    )
  );
}

export function buildJoinPeriodoComparativo(period = {}) {
  const sanitized = sanitizeJoinPeriodo(period);
  const inicio = parseIsoDate(sanitized.dataInicio);
  const fim = parseIsoDate(sanitized.dataFim);
  const monthsSpan = getInclusiveMonthSpan(inicio, fim);

  return {
    dataInicio: shiftIsoDateByMonths(sanitized.dataInicio, -monthsSpan),
    dataFim: shiftIsoDateByMonths(sanitized.dataFim, -monthsSpan),
  };
}

export function getJoinGranularidadeEvolucao(period = {}) {
  // Mantemos sempre por dia para ficar consistente com o dashboard.
  // Mesmo em periodos maiores, o eixo continua em DIA (apenas com menos ticks visiveis).
  return "DIA";
}

export function formatPeriodoDescricao(period = {}) {
  const sanitized = sanitizeJoinPeriodo(period);
  return `${formatIsoDateToDisplay(sanitized.dataInicio)} a ${formatIsoDateToDisplay(sanitized.dataFim)}`;
}

export function calculateChange(currentValue, previousValue) {
  const current = toNumber(currentValue);
  const previous = toNumber(previousValue);
  const delta = current - previous;

  if (previous === 0) {
    return {
      delta,
      percent: current === 0 ? 0 : null,
      direction: delta === 0 ? "neutral" : delta > 0 ? "up" : "down",
    };
  }

  return {
    delta,
    percent: Math.abs((delta / previous) * 100),
    direction: delta === 0 ? "neutral" : delta > 0 ? "up" : "down",
  };
}

export function flattenJoinProducaoAgrupada(producaoAgrupada = []) {
  if (!Array.isArray(producaoAgrupada)) return [];

  const rows = [];
  producaoAgrupada.forEach((banco) => {
    const bancoId = String(banco?.bancoId ?? "");
    const bancoNome = String(banco?.bancoNome || "Sem banco");
    const convenios = Array.isArray(banco?.convenios) ? banco.convenios : [];

    convenios.forEach((convenio) => {
      const convenioId = String(convenio?.convenioId ?? "");
      const convenioNome = String(convenio?.convenioNome || "Sem convenio");
      const tipos = Array.isArray(convenio?.tipos) ? convenio.tipos : [];

      tipos.forEach((tipo) => {
        const producaoBrutaTotal = toNumber(tipo?.producaoBrutaTotal);
        const producaoLiquidaTotal = toNumber(tipo?.producaoLiquidaTotal);
        const producaoComissionadaTotal = toNumber(tipo?.producaoComissionadaTotal);
        rows.push({
          bancoId,
          bancoNome,
          convenioId,
          convenioNome,
          tipoConvenio: normalizeTipoProduto(tipo?.tipoConvenio),
          producaoBrutaTotal,
          producaoLiquidaTotal,
          producaoComissionadaTotal,
          percentualRepasse: toNumber(tipo?.percentualRepasse),
          spread: toNumber(tipo?.spread),
          contratosAprovados: toNumber(tipo?.contratosAprovados),
          contratosNegados: toNumber(tipo?.contratosNegados),
          contratosCancelados: toNumber(tipo?.contratosCancelados),
          contratosEmAndamento: toNumber(tipo?.contratosEmAndamento),
          _pesoRepasse: producaoComissionadaTotal || producaoLiquidaTotal || producaoBrutaTotal || 1,
          _pesoSpread: producaoBrutaTotal || producaoComissionadaTotal || 1,
        });
      });
    });
  });

  return rows;
}

export function aggregateJoinRows(rows = [], grouping = { banco: true, convenio: true, tipo: false }) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const resolvedGrouping = {
    banco: grouping?.banco !== false,
    convenio: grouping?.convenio !== false,
    tipo: grouping?.tipo === true,
  };

  if (!resolvedGrouping.banco && !resolvedGrouping.convenio && !resolvedGrouping.tipo) {
    resolvedGrouping.banco = true;
  }

  const grouped = new Map();

  rows.forEach((row) => {
    const groupKey = [
      resolvedGrouping.banco ? row.bancoId || row.bancoNome : "*",
      resolvedGrouping.convenio ? row.convenioId || row.convenioNome : "*",
      resolvedGrouping.tipo ? row.tipoConvenio : "*",
    ].join("|");

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        groupKey,
        bancoId: resolvedGrouping.banco ? row.bancoId : "",
        bancoNome: resolvedGrouping.banco ? row.bancoNome : "Todos os bancos",
        convenioId: resolvedGrouping.convenio ? row.convenioId : "",
        convenioNome: resolvedGrouping.convenio ? row.convenioNome : "Todos os convenios",
        tipoConvenio: resolvedGrouping.tipo ? row.tipoConvenio : "Todos os tipos de produto",
        producaoBrutaTotal: 0,
        producaoLiquidaTotal: 0,
        producaoComissionadaTotal: 0,
        percentualRepasse: 0,
        spread: 0,
        contratosAprovados: 0,
        contratosNegados: 0,
        contratosCancelados: 0,
        contratosEmAndamento: 0,
        _pesoRepasseTotal: 0,
        _pesoRepasseValor: 0,
        _pesoSpreadTotal: 0,
        _pesoSpreadValor: 0,
      });
    }

    const current = grouped.get(groupKey);
    METRIC_FIELDS.forEach((field) => {
      current[field] += toNumber(row[field]);
    });

    current._pesoRepasseTotal += toNumber(row._pesoRepasse);
    current._pesoRepasseValor += toNumber(row.percentualRepasse) * toNumber(row._pesoRepasse);
    current._pesoSpreadTotal += toNumber(row._pesoSpread);
    current._pesoSpreadValor += toNumber(row.spread) * toNumber(row._pesoSpread);
  });

  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      percentualRepasse: row._pesoRepasseTotal > 0 ? row._pesoRepasseValor / row._pesoRepasseTotal : 0,
      spread: row._pesoSpreadTotal > 0 ? row._pesoSpreadValor / row._pesoSpreadTotal : 0,
      contratosTotal:
        row.contratosAprovados +
        row.contratosNegados +
        row.contratosCancelados +
        row.contratosEmAndamento,
    }))
    .sort((left, right) => {
      const primary = right.producaoBrutaTotal - left.producaoBrutaTotal;
      if (primary !== 0) return primary;
      return String(left.groupKey).localeCompare(String(right.groupKey));
    });
}

export function buildJoinComparisonMap(rows = []) {
  return new Map((rows || []).map((row) => [String(row.groupKey || ""), row]));
}

export function sumJoinContracts(rows = []) {
  return (rows || []).reduce(
    (acc, row) => ({
      contratosAprovados: acc.contratosAprovados + toNumber(row?.contratosAprovados),
      contratosNegados: acc.contratosNegados + toNumber(row?.contratosNegados),
      contratosCancelados: acc.contratosCancelados + toNumber(row?.contratosCancelados),
      contratosEmAndamento: acc.contratosEmAndamento + toNumber(row?.contratosEmAndamento),
      contratosTotal:
        acc.contratosTotal +
        toNumber(row?.contratosAprovados) +
        toNumber(row?.contratosNegados) +
        toNumber(row?.contratosCancelados) +
        toNumber(row?.contratosEmAndamento),
    }),
    {
      contratosAprovados: 0,
      contratosNegados: 0,
      contratosCancelados: 0,
      contratosEmAndamento: 0,
      contratosTotal: 0,
    }
  );
}

export function filterJoinRowsBySearch(rows = [], term = "") {
  const normalized = String(term || "").trim().toLowerCase();
  if (!normalized) return rows;
  return (rows || []).filter((row) =>
    [row.bancoNome, row.convenioNome, row.tipoConvenio]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalized))
  );
}
